import { useQuery } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { QUERY_KEYS } from '~shared/api/query-keys';
import { normalizePaymentRows } from '~shared/helpers/normalize-payments';

export const STUDENT_PAYMENT_HISTORY_DATE_FIELD = {
  ALL: 'all',
  CREATION: 'createdAt',
  DUE: 'dueDate',
  PAID: 'paymentDate',
} as const;

export type StudentPaymentHistoryDateField =
  (typeof STUDENT_PAYMENT_HISTORY_DATE_FIELD)[keyof typeof STUDENT_PAYMENT_HISTORY_DATE_FIELD];

export type PaymentHistorySortOrder = 'asc' | 'desc';

export const PAYMENT_HISTORY_SORT_ORDER: Record<
  'ASC' | 'DESC',
  PaymentHistorySortOrder
> = {
  ASC: 'asc',
  DESC: 'desc',
};

export interface GetStudentPaymentHistoryParams {
  studentId: string;
  startDate: string;
  endDate: string;
  dateField?: StudentPaymentHistoryDateField;
  /** API default is descending (newest first) when omitted */
  sortOrder?: PaymentHistorySortOrder;
  status?: string;
}

export interface StudentPaymentHistoryInvoice {
  _id: string;
  appliedDiscount: number;
  attemptCount: number;
  baseAmount: string;
  className: string;
  discount: string;
  failureReason: null | string;
  finalAmount: string;
  transactionFeeAmount?: number;
  internalTransactionId: null | string;
  invoiceDate: string;
  invoiceId: null | string;
  lastAttemptDate: null;
  paidAmount: string;
  paymentDate?: string;
  dueDate?: string;
  paymentMethod?: string;
  paymentProvider: string;
  payments: Array<Payment>;
  refundId: string | null;
  refundReason: string | null;
  metadata?: {
    description?: string;
    creditMemo?: boolean;
    sourceInvoiceId?: string;
  };
  /** Present on some payment-history API payloads for expanded line-item rows. */
  isLineItemRow?: boolean;
  status:
    | 'cancelled'
    | 'void'
    | 'paid'
    | 'failed'
    | 'free'
    | 'partially paid'
    | 'scheduled'
    | 'invoice scheduled'
    | 'pending'
    | 'invalid';
}

export interface Payment {
  amount: number;
  date: string;
  method: string;
  paymentIntentId?: string;
  checkNumber?: string;
  note?: string;
  recordedBy?: string;
  recordedByEmail?: string;
  recordedAt?: string;
  entryDate?: string;
  overpaymentAmount?: number;
  reversedAt?: string;
  isReversal?: boolean;
}

export const getStudentPaymentHistory = async ({
  studentId,
  ...params
}: GetStudentPaymentHistoryParams) => {
  const response = await baseAPI.get<Array<StudentPaymentHistoryInvoice>>(
    `/subscription-invoices/payment-history/student/${studentId}`,
    { params },
  );
  return normalizePaymentRows(response.data);
};

export const useGetStudentPaymentHistory = (
  params: GetStudentPaymentHistoryParams,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.STUDENT_PAYMENT_HISTORY(params),
    queryFn: () => getStudentPaymentHistory(params),
    staleTime: 1000 * 60 * 3, // 3 minutes
    refetchInterval: 1000 * 60 * 3, // Auto refresh every 3 minutes
  });
};
